import React, { useState, useEffect } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Typography,
  Autocomplete,
  TextField,
  MenuItem,
  Popper,
  Grid,
} from "@mui/material";
import { CheckCircleRounded } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import useFetchUserRole from "../../hooks/useFetchUserRole";

interface AssignRoleProps {
  selectedRolesIds: number[];
  onRoleChange: (roleIds: number[]) => void;
  disabled?: boolean;
  loading?: boolean;
  error?: string;
  label?: string;
  placeholder?: string;
  viewMode?: boolean;
  assignedRoles?: any[];
}

const AssignRole: React.FC<AssignRoleProps> = ({
  selectedRolesIds,
  onRoleChange,
  disabled = false,
  loading = false,
  error = "",
  label = "Assign Roles",
  placeholder = "Select...",
  viewMode = false,
  assignedRoles = [],
}) => {
  const theme = useTheme();
  const [selectedIds, setSelectedIds] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Fetch user roles for dropdown
  const [userRoleData] = useFetchUserRole({ setIsLoading });

  useEffect(() => {
    setSelectedIds(selectedRolesIds);
  }, [selectedRolesIds]);

  useEffect(() => {
    console.log("userRoleData", userRoleData);
    console.log("selectedIds", selectedIds);
  }, [userRoleData, selectedIds]);

  const handleChange = (_event: any, newValue: any) => {
    const newSelectedRoleIds = newValue.map((role: { id: any }) => role.id);
    setSelectedIds(newSelectedRoleIds);

    onRoleChange(newSelectedRoleIds);
  };

  const renderRolesPopper = (props: any) => (
    <Popper
      {...props}
      className="filter-autocomplete-zindex"
      anchorEl={document.getElementById("assign-roles-autocomplete")}
    />
  );

  // View mode rendering
  if (viewMode) {
    return (
      <Box className="text-box-card-white" sx={{ mt: 2 }}>
        <Box className="text-box-header">
          <h3>{label}</h3>
        </Box>
        <Grid item xs={12}>
          <label className="label-text">Roles</label>
          <Box sx={{ mt: 1 }}>
            {assignedRoles && assignedRoles.length > 0 ? (
              <div className="tags-tooltip-parent">
                {assignedRoles.map((role: any, index: number) => (
                  <Box className="chip" key={role.id}>
                    <Typography variant="body2" fontWeight="medium">
                      {userRoleData.find(
                        (userRole) => userRole.id === role.user_role_id
                      )?.role_name || ""}
                    </Typography>
                  </Box>
                ))}
              </div>
            ) : (
              <Box sx={{ color: "text.secondary", fontStyle: "italic" }}>
                No roles assigned
              </Box>
            )}
          </Box>
        </Grid>
      </Box>
    );
  }

  return (
    <>
      <div className="form-group non-flex">
        <div className="label-text">{label}</div>
        <div className="form-input">
          <Autocomplete
            id="assign-roles-autocomplete"
            PopperComponent={renderRolesPopper}
            fullWidth={true}
            className="form-control-autocomplete"
            multiple
            options={userRoleData || []}
            getOptionLabel={(option: any) => option?.role_name || ""}
            disableCloseOnSelect
            disabled={disabled}
            onChange={handleChange}
            renderInput={(params) => (
              <>
                <div className="autocomplete-chips-direction">
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={placeholder}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </div>
              </>
            )}
            renderTags={(value, getTagProps) => {
              return (
                <div className="align-from-left">
                  {value?.map((option: any, index: number) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option?.id || index}
                      label={option?.role_name}
                      sx={{
                        backgroundColor: theme.palette.primary.light,
                        color: theme.palette.primary.contrastText,
                        "& .MuiChip-deleteIcon": {
                          color: theme.palette.primary.contrastText,
                        },
                      }}
                    />
                  ))}
                </div>
              );
            }}
            renderOption={(props, option: any, { selected }) => (
              <MenuItem
                {...props}
                key={`${option?.id}-${option?.role_name}`}
                value={option?.role_name}
                sx={{ justifyContent: "space-between" }}
              >
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {option?.role_name}
                  </Typography>
                </Box>
                {selected ? <CheckCircleRounded color="info" /> : null}
              </MenuItem>
            )}
            value={
              selectedRolesIds
                .map((id) =>
                  (userRoleData || []).find((option) => option.id === id)
                )
                .filter((option) => option !== undefined) || null
            }
            isOptionEqualToValue={(option: any, value: any) =>
              option.id === value.id
            }
          />
        </div>
      </div>
    </>
  );
};

export default AssignRole;
